// frontend/src/pages/TaxDocumentReview.tsx
//
// TAX-PARSE-2 — CPA tax document review page. Renders inside AppShell as a
// protected child route at /deals/:dealId/tax. Four tabs: Upload |
// Extracted Fields | Cross-Verify | MIR Findings.
//
// The route param is `dealId`; it is passed as `deal_id` to every
// tax endpoint (see api/tax.ts) — QoE's unit of work is the deal, not the
// OTCID `engagement_id` concept.

import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { toast } from 'sonner'
import { ChevronLeft } from 'lucide-react'

import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { EmptyState } from '@/components/ui/empty-state'
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table'
import { formatApiError } from '@/utils/httpErrors'
import {
  listTaxDocuments,
  getTaxEvidence,
  type TaxDocumentResponse,
  type TaxDocumentSummary,
  type TaxEvidenceItem,
  type TaxDiscrepancy,
} from '@/api/tax'
import { TaxUploadDropzone } from '@/components/tax/TaxUploadDropzone'
import { TaxFieldTable } from '@/components/tax/TaxFieldTable'
import { CrossVerifyPanel } from '@/components/tax/CrossVerifyPanel'
import { TaxEvidenceList } from '@/components/tax/TaxEvidenceList'

function fmtFieldName(key: string): string {
  return key
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

export function TaxDocumentReview() {
  const { dealId: dealIdParam } = useParams<{ dealId: string }>()
  const dealId = dealIdParam ?? ''

  const [activeTab, setActiveTab] = useState('upload')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [retryCount, setRetryCount] = useState(0)

  const [documents, setDocuments] = useState<TaxDocumentSummary[]>([])
  const [evidence, setEvidence] = useState<TaxEvidenceItem[]>([])
  // Full field detail is only available for docs uploaded this session —
  // GET /v1/tax/documents returns summaries only (no `fields`).
  const [uploadedDocs, setUploadedDocs] = useState<TaxDocumentResponse[]>([])

  useEffect(() => {
    if (!dealId) return
    let cancelled = false

    async function load() {
      setLoading(true)
      setError(null)
      try {
        const [docsRes, evidenceRes] = await Promise.all([
          listTaxDocuments(dealId),
          getTaxEvidence(dealId),
        ])
        if (cancelled) return
        setDocuments(docsRes)
        setEvidence(evidenceRes)
      } catch (err) {
        if (cancelled) return
        setError(formatApiError(err))
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [dealId, retryCount])

  const handleUploaded = (doc: TaxDocumentResponse) => {
    setUploadedDocs((prev) => [doc, ...prev])
    setDocuments((prev) => [
      {
        doc_id: doc.doc_id,
        form_type: doc.form_type,
        tax_year: doc.tax_year,
        confidence: doc.confidence,
        needs_verification: doc.needs_verification,
      },
      ...prev.filter((d) => d.doc_id !== doc.doc_id),
    ])
    setActiveTab('fields')
  }

  const handleEvidenceAdded = (item: TaxEvidenceItem, findingNumber: number) => {
    setEvidence((prev) =>
      prev.map((e) => (e.id === item.id ? { ...e, notes: `Added as MIR finding #${findingNumber}` } : e)),
    )
  }

  const handleAddDiscrepancyToMir = (discrepancy: TaxDiscrepancy) => {
    toast.info(
      `Locate "${fmtFieldName(discrepancy.field)}" FY${discrepancy.year} in MIR Findings below to add it.`,
    )
    setActiveTab('mir')
  }

  // ── Loading ──────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="p-6 space-y-4 max-w-7xl mx-auto">
        <Skeleton className="h-5 w-36" />
        <Skeleton className="h-8 w-72" />
        <Skeleton className="h-10 w-80" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  // ── Error ────────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <Alert variant="destructive">
          <AlertDescription className="flex items-center justify-between">
            <span>{error}</span>
            <Button variant="outline" size="sm" onClick={() => setRetryCount((c) => c + 1)} className="ml-4">
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  if (!dealId) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <Alert variant="destructive">
          <AlertDescription>No deal selected. Navigate here from a deal page.</AlertDescription>
        </Alert>
      </div>
    )
  }

  const allDocIds = documents.map((d) => d.doc_id)

  return (
    <div className="p-6 space-y-4 max-w-7xl mx-auto">
      <Link
        to={`/deals/${dealId}`}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ChevronLeft className="h-4 w-4" />
        Back to Deal
      </Link>

      <div>
        <h1 className="text-2xl font-bold" style={{ color: 'var(--color-primary)' }}>
          Tax Document Review
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          {documents.length} document{documents.length !== 1 ? 's' : ''} on file ·{' '}
          {evidence.length} evidence item{evidence.length !== 1 ? 's' : ''}
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(String(v))}>
        <TabsList className="max-w-full flex-nowrap justify-start overflow-x-auto overflow-y-hidden">
          <TabsTrigger value="upload">Upload</TabsTrigger>
          <TabsTrigger value="fields">
            <span className="md:hidden">Fields</span>
            <span className="hidden md:inline">Extracted Fields</span>
          </TabsTrigger>
          <TabsTrigger value="cross-verify">
            <span className="md:hidden">Verify</span>
            <span className="hidden md:inline">Cross-Verify</span>
          </TabsTrigger>
          <TabsTrigger value="mir">
            <span className="md:hidden">MIR</span>
            <span className="hidden md:inline">MIR Findings</span>
          </TabsTrigger>
        </TabsList>

        {/* ── Upload tab ───────────────────────────────────────────────── */}
        <TabsContent value="upload" className="mt-4 space-y-4">
          <TaxUploadDropzone dealId={dealId} onUploaded={handleUploaded} />

          {documents.length > 0 && (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Form</TableHead>
                    <TableHead>Tax Year</TableHead>
                    <TableHead>Confidence</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {documents.map((doc) => (
                    <TableRow key={doc.doc_id}>
                      <TableCell className="font-medium">{doc.form_type.toUpperCase()}</TableCell>
                      <TableCell>{doc.tax_year ?? '—'}</TableCell>
                      <TableCell className="tabular-nums">{Math.round(doc.confidence * 100)}%</TableCell>
                      <TableCell>
                        {doc.needs_verification ? (
                          <span className="text-xs font-semibold text-amber-700">Needs verification</span>
                        ) : (
                          <span className="text-xs font-semibold text-green-700">Verified</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>

        {/* ── Extracted Fields tab ─────────────────────────────────────── */}
        <TabsContent value="fields" className="mt-4 space-y-6">
          {uploadedDocs.length === 0 ? (
            <EmptyState
              title="No extracted fields to show"
              description="Upload a tax document in this session to see its extracted fields here."
            />
          ) : (
            uploadedDocs.map((doc) => (
              <div key={doc.doc_id} className="space-y-2">
                <p className="text-sm font-semibold">
                  {doc.form_type.toUpperCase()}
                  {doc.tax_year ? ` · FY${doc.tax_year}` : ''}
                </p>
                <TaxFieldTable document={doc} />
              </div>
            ))
          )}
        </TabsContent>

        {/* ── Cross-Verify tab ─────────────────────────────────────────── */}
        <TabsContent value="cross-verify" className="mt-4">
          <CrossVerifyPanel
            dealId={dealId}
            docIds={allDocIds}
            onAddDiscrepancyToMir={handleAddDiscrepancyToMir}
          />
        </TabsContent>

        {/* ── MIR Findings tab ─────────────────────────────────────────── */}
        <TabsContent value="mir" className="mt-4">
          <TaxEvidenceList dealId={dealId} evidence={evidence} onAdded={handleEvidenceAdded} />
        </TabsContent>
      </Tabs>
    </div>
  )
}

export default TaxDocumentReview
